import React, { useEffect, useMemo, useRef, useState } from "react";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  Button,
  GridTable,
  SecondaryHeader,
  SecondaryHeaderButtonWrap,
  SecondaryHeaderInfo,
  SecondaryHeaderSearchField,
  Spinner,
} from "olive-react-myoneui"; // Assuming Loader exists here
import PaginationTemplate from "../component/grid-table/PaginationTemplate";
import TextTemplate from "../component/grid-table/TextTemplate";
import { penEditIcon, removeIcon } from "../../../constants/icons";
import NameTemplate from "../component/grid-table/NameTemplate";
import ConfirmBox from "../component/ConfirmBox";
import AddOffcanvas from "./AddOffcanvas";
import EditOffcanvas from "./EditOffcanvas";
import {
  getSubject,
  updateSubject,
  deleteSubject,
} from "../../../api/subjectApis";
import { debounce } from "../../../utils/helper";
import DataNotFound from "../component/DataNotFound";

function getErrorMessage(error) {
  if (!error) return "Something went wrong";
  if (error.response && error.response.data) {
    const data = error.response.data;
    if (data.message) return data.message;
    if (data.errors && Array.isArray(data.errors) && data.errors.length > 0) {
      return data.errors[0].msg || data.errors[0];
    }
  }
  return error.message || String(error);
}

export default function Subjects() {
  const [showConfirmBox, setShowConfirmBox] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [showEdit, setShowEdit] = useState(false);

  const [loading, setLoading] = useState(false);
  const [operationLoading, setOperationLoading] = useState(false); // For add/edit/delete

  const [name, setName] = useState("");
  const [image, setImage] = useState("");
  const [newImage, setNewImage] = useState(null);
  const [subjectId, setSubjectId] = useState(null);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalItems, setTotalItems] = useState(0);
  const [data, setData] = useState([]);

  const [search, setSearch] = useState("");
  const [searchText, setSearchText] = useState("");

  const apiCallInProgress = useRef(false);

  const debouncedSetSearchText = useMemo(
    () =>
      debounce((val) => {
        setPage(1);
        setSearchText(val);
      }, 500),
    [],
  );

  const onSearchChange = (e) => {
    const val = e.target.value;
    setSearch(val);
    debouncedSetSearchText(val);
  };

  const onClearSearch = () => {
    setSearch("");
    setSearchText("");
    setPage(1);
  };

  function formatDate(dateString) {
    const date = new Date(dateString);
    const options = {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    };
    return date
      .toLocaleString("en-GB", options)
      .replace("AM", "am")
      .replace("PM", "pm");
  }

  async function fetchSubjects() {
    if (apiCallInProgress.current) return;
    apiCallInProgress.current = true;
    setLoading(true);
    try {
      const params = {
        page,
        limit: pageSize,
        ...(searchText ? { title: searchText } : {}),
      };

      const response = await getSubject(params);

      if (response && response.data) {
        const list = (response.data.data || []).map((item) => ({
          ...item,
          createdAt: formatDate(item.createdAt),
        }));

        setData(list);

        if (response.data.pagination) {
          setTotalItems(response.data.pagination.totalItems);
          setPageSize(response.data.pagination.pageSize);
        }
      }
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setLoading(false);
      apiCallInProgress.current = false;
    }
  }

  useEffect(() => {
    fetchSubjects();
  }, [page, pageSize, searchText]);

  const handlePageChange = (newPage) => {
    if (!loading && !operationLoading) setPage(newPage);
  };

  const handlePageSizeChange = (newSize) => {
    if (!loading && !operationLoading && newSize !== pageSize) {
      setPageSize(newSize);
      setPage(1);
    }
  };

  const handleDeleteSubject = (data) => {
    if (!operationLoading) {
      setShowConfirmBox(true);
      if (data) setSubjectId(data._id);
    }
  };

  const handleHideConfirmBox = () => setShowConfirmBox(false);

  const handleEditSubject = (data) => {
    if (!operationLoading) {
      setShowEdit(true);
      if (data) {
        setImage(data.imageUrl);
        setName(data.title);
        setSubjectId(data._id);
      }
    }
  };

  const handleClose = () => {
    if (!operationLoading) {
      setShowAdd(false);
      setShowEdit(false);
      setName("");
      setImage("");
      setNewImage(null);
      setSubjectId(null);
    }
  };

  const updateSubjects = async (image, name, id) => {
    console.log("Updating subject with image:", image);
    console.log("Updating subject with newImage:", newImage);

    let formData = new FormData();
    formData.append("title", name);
    if (newImage && Array.isArray(newImage)) {
      newImage.forEach((file) => {
        formData.append("image", file);
      });
    }

    setOperationLoading(true);
    try {
      const response = await updateSubject(formData, id);
      if (response) {
        toast.success("Subject updated successfully");
        handleClose();
        await fetchSubjects();
      }
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setOperationLoading(false);
    }
  };

  const deleteSubjectById = async () => {
    setOperationLoading(true);
    try {
      const response = await deleteSubject(subjectId);
      if (response) {
        toast.success("Subject deleted successfully");
        setShowConfirmBox(false);
        setSubjectId(null);
        await fetchSubjects();
      }
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setOperationLoading(false);
    }
  };

  const handleAddSubjectSuccess = async () => {
    // Called by AddOffcanvas when add is successful
    setShowAdd(false);
    await fetchSubjects();
  };

  const columnData = [
    {
      id: 0,
      visible: true,
      label: "S No.",
      width: "1fr",
      sortable: false,
      className: "grid-table-title",
      cellRenderer: ({ rowIndex }) => <TextTemplate value={rowIndex} />,
    },
    {
      id: 1,
      field: "title",
      visible: true,
      label: "Subjects Title",
      width: "4fr",
      sortable: false,
      className: "grid-table-title",
      cellRenderer: NameTemplate,
    },
    {
      id: 2,
      field: "createdAt",
      visible: true,
      label: "Created At",
      width: "4fr",
      sortable: false,
      className: "grid-table-title",
      cellRenderer: TextTemplate,
    },
    {
      id: 4,
      label: "Action",
      width: "3fr",
      sortable: false,
      resizable: false,
      className: "grid-header-text-center",
      cellRenderer: ({ data }) => (
        <div
          className="rgt-cell-inner rgt-text-truncate program-edit-hover justify-content-center"
          title=""
        >
          <Button
            variant="white"
            className="btn-round"
            onClick={() => handleEditSubject(data)}
            disabled={operationLoading}
          >
            {penEditIcon} Edit
          </Button>
          <Button
            variant="white"
            className="btn-round"
            onClick={() => handleDeleteSubject(data)}
            disabled={operationLoading}
          >
            {removeIcon} Delete
          </Button>
        </div>
      ),
    },
  ];

  return (
    <>
      <ToastContainer position="top-right" autoClose={3000} />
      <div className="content-wrapper">
        <SecondaryHeader>
          <SecondaryHeaderInfo
            SecondaryHeaderTitle="Subjects"
            SecondaryHeaderText={`${totalItems} Subjects`}
            SecondaryText
          />
          <SecondaryHeaderSearchField
            placeholder="Search Subjects..."
            value={search}
            onChange={onSearchChange}
            onClear={onClearSearch}
            disabled={loading || operationLoading}
          />
          <SecondaryHeaderButtonWrap>
            <Button
              variant="secondary"
              onClick={() => setShowAdd(true)}
              disabled={loading || operationLoading}
            >
              Add
            </Button>
          </SecondaryHeaderButtonWrap>
        </SecondaryHeader>

        <div className="content-block p-2">
          {(loading || operationLoading) && (
            <div className="spinner-wrapper">
              <Spinner animation="border" />
            </div>
          )}

          {data.length > 0 && !loading && (
            <GridTable
              key={data.length}
              searchText={searchText}
              highlightSearch={false}
              enableColumnsReorder={false}
              columns={columnData}
              onRowsRequest={() => ({
                rows: data,
                totalRows: totalItems,
              })}
              page={page}
              pageSize={pageSize}
              setPage={handlePageChange}
              setPageSize={handlePageSizeChange}
              totalPages={Math.ceil(totalItems / pageSize)}
              onLoad={(tm) => {}}
              components={{
                Footer: (props) => (
                  <PaginationTemplate
                    {...props}
                    page={page}
                    pageSize={pageSize}
                    totalPages={Math.ceil(totalItems / pageSize)}
                    onPageChange={handlePageChange}
                    onPageSizeChange={handlePageSizeChange}
                    disabled={loading || operationLoading} // Disable pagination when loading
                  />
                ),
              }}
            />
          )}

          {!loading && data.length === 0 && (
            <DataNotFound title="No subjects found" nochange={true} />
          )}
        </div>
      </div>

      <ConfirmBox
        show={showConfirmBox}
        question="Are you sure you want to delete subjects?"
        okText="Delete"
        cancelText="Cancel"
        onHide={handleHideConfirmBox}
        onSubmit={deleteSubjectById}
        loading={operationLoading} // Disable buttons in ConfirmBox during operation
      />

      <AddOffcanvas
        show={showAdd}
        onClose={handleClose}
        getallSubjects={handleAddSubjectSuccess}
        image={image}
        setImage={setImage}
        name={name}
        setName={setName}
        loading={operationLoading}
      />

      <EditOffcanvas
        show={showEdit}
        onClose={handleClose}
        updateSubjects={updateSubjects}
        image={image}
        setImage={setImage}
        name={name}
        setName={setName}
        subjectId={subjectId}
        loading={operationLoading}
        setNewImage={setNewImage}
      />
    </>
  );
}
